import _ from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useSelector} from '../../../state/hooks';
import {useSyncedPollingSubscriber} from '../../../state/polling/hooks';
import * as RunSelectorRunActions from '../../../state/runSelectorRuns/actions';
import {EMPTY_DATA} from '../../../state/runSelectorRuns/reducer';
import {useCachedDataWithOrigin} from '../../../util/cache';
import {useFirstTimingEventWhen} from '../../../util/profiler/react';
import * as RunTypes from '../../../util/runTypes';
import {RunSelectorRunsPartialVars} from '../types';
import {
  useRunSelectorRunsDataQuery,
  useVariables,
} from './useRunSelectorRunsDataQuery';
import {useRunsPaging} from './useRunsPaging';

export type UseRunSelectorRunsArgs = {
  /** Indicates whether this is a root query for an entire run set, or a sub-query for an expanded group */
  isRootQuery: boolean;
  /** Unique ID for the runs data managed by this instance of useRunSelectorRuns */
  queryId: string;
  /** Determines how many runs will be returned on initial load and when loading more runs */
  pageSize: number;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
  /** If true, will not query for runs */
  skip?: boolean;
};

type RunSelectorRunsResult = {
  /**
   * Indicates whether we're loading runs. Could be from explicit user
   * interactions (e.g. searching, paging, ...) or automatic polling.
   */
  isLoadingRuns: boolean;
  /**
   * Indicates whether we're loading for a polled request.
   * Allows more granular control for loading UI.
   * @todo update type to be required once off of runs-low
   */
  isLoadingRunsPolled?: boolean;
  /**
   * Function for paging. When navigating between pages,
   * call this with the new page number to ensure runs for
   * that page will be loaded (as necessary; may be cached)
   * @todo update type to be required once off of runs-low
   */
  loadMoreRuns?: (page: number) => void;
  /**
   * Detailed runs data. Only contains enough data to
   * satisfy the current paging criteria
   */
  runs: RunTypes.Run[];
  /**
   * Total number of runs that would match the query,
   * regardless of the paging limit
   */
  totalRuns: number;
};

export const useRunSelectorRuns = ({
  isRootQuery,
  queryId,
  pageSize,
  partialVariables,
  shouldOnlyShowSelected,
  skip = false,
}: UseRunSelectorRunsArgs): RunSelectorRunsResult => {
  const {loadMoreRuns, limit} = useRunsPaging(pageSize);

  const {variables, deltaVariablesRef} = useVariables({
    limit,
    partialVariables,
    shouldOnlyShowSelected,
  });
  const {data, isLoadingRuns, refetch} = useRunSelectorRunsDataQuery({
    variables,
    skip,
    deltaVariablesRef,
  });

  // Read run selector runs data from redux
  const dataFromRedux = useSelector(state => state.runSelectorRuns[queryId]);
  const {runs, totalRuns, lastUpdatedAt} = dataFromRedux ?? EMPTY_DATA;

  const {data: finalData, origin} = useCachedDataWithOrigin<
    typeof dataFromRedux
  >({
    namespace: 'RunsSelectorRunsData',
    key: variables,
    dataFromServer: dataFromRedux,
    waitingForDataFromServer: isLoadingRuns && isRootQuery,
  });

  // Write updates to redux store
  const dispatch = useDispatch();
  const queryResults = data?.project?.runs;
  useEffect(() => {
    if (queryResults) {
      dispatch(
        RunSelectorRunActions.processQueryResults(queryId, queryResults)
      );
    }
  }, [dispatch, queryId, queryResults]);

  useEffect(() => {
    if (
      finalData != null &&
      origin === 'cache' &&
      isRootQuery &&
      !_.isEqual(finalData, dataFromRedux)
    ) {
      dispatch(
        RunSelectorRunActions.loadRunSelectorRunsFromCache(queryId, finalData)
      );
    }
  }, [dispatch, queryId, finalData, origin, dataFromRedux, isRootQuery]);

  // Clear redux state on unmount/group change
  useEffect(() => {
    return () => {
      if (!skip) {
        dispatch(RunSelectorRunActions.removeQuery(queryId));
      }
    };
  }, [dispatch, queryId, skip]);

  // Perf logging
  const isInitialLoadedRef = useRef(false);
  useFirstTimingEventWhen('run_selector_initial_loaded', () => {
    if (isRootQuery && dataFromRedux != null && !isInitialLoadedRef.current) {
      isInitialLoadedRef.current = true;
      return true;
    }
    return false;
  });

  // Keep delta vars ref up to date
  useEffect(() => {
    deltaVariablesRef.current = {
      currentRuns: runs.map(run => run.id),
      lastUpdated: new Date(lastUpdatedAt).toISOString(),
    };
  }, [runs, lastUpdatedAt, deltaVariablesRef]);

  // Subscribe to synced polling so RunSelector updates in sync with Charts
  const {lastTickedAt, stopTaskTimer} =
    useSyncedPollingSubscriber('RunSelector');
  const [isPollTaskActive, setIsPollTaskActive] = useState(false);

  // Refetch on poll tick
  useEffect(() => {
    if (!skip && lastTickedAt != null && !isLoadingRuns) {
      setIsPollTaskActive(true);
      refetch();
    }
  }, [lastTickedAt]); // eslint-disable-line react-hooks/exhaustive-deps -- should ONLY trigger on poll tick!

  // Record when polled requests are loaded
  useEffect(() => {
    if (!isLoadingRuns && isPollTaskActive) {
      setIsPollTaskActive(false);
      stopTaskTimer();
    }
  }, [isPollTaskActive, isLoadingRuns, stopTaskTimer]);

  const results = useMemo(
    () => ({
      isLoadingRuns: isLoadingRuns && origin === 'server',
      isLoadingRunsPolled: isPollTaskActive,
      loadMoreRuns,
      runs,
      totalRuns,
    }),
    [isLoadingRuns, isPollTaskActive, loadMoreRuns, runs, totalRuns, origin]
  );

  return results;
};
